/**
 * WorkOrderProcessesPanel — F5.8b Fase B
 *
 * Muestra los PieceInstances (cada PI = cada pieza física del trabajo) de una
 * WO con su pipeline de procesos. Reusa PipelineVertical y DefinirProcesosForm
 * de /produccion, evitando duplicar 900+ líneas de lógica.
 *
 * Acciones: start, complete, uncomplete (undo simplificado por ahora).
 *
 * Props:
 *   workOrderId      string  — id de la WO
 *   onPiUpdated      fn      — opcional: callback al actualizar un PI (refrescar parent)
 */
import React, { useCallback, useEffect, useState } from "react";
import backend from "../../backend";
import {
  PipelineVertical,
  ConfirmarAccion,
} from "../produccion/ProduccionV2.interacciones";
import DimensionalControlPanel from "./DimensionalControlPanel";

const STATUS_LABEL = {
  pending: "⏳ Pendiente",
  in_process: "⚙ En proceso",
  ready: "✓ Lista",
  delivered: "📦 Entregada",
  invoiced: "💰 Facturada",
};

const STATUS_COLOR = {
  pending: "#888",
  in_process: "#3498db",
  ready: "#1abc9c",
  delivered: "#2ecc71",
  invoiced: "#16a085",
};

// F5.8b — Mini-componente comparativo de costo (read-only, no toca catálogo)
const fmtCLP = n => `$${Number(n || 0).toLocaleString("es-CL")}`;

const CostAnalysisBox = ({ pieceId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // pi.piece puede venir como string (id) o como objeto populado { _id, name }
  const id = typeof pieceId === "object" && pieceId !== null
    ? (pieceId._id || pieceId.id || null)
    : pieceId;

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await backend.get(`piece/${id}/cost-analysis`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (!id) {
    return (
      <div style={{ padding: "10px 14px", fontSize: 11, color: "#aaa", fontStyle: "italic" }}>
        Pieza sin vínculo al catálogo — no se puede calcular costo.
      </div>
    );
  }
  if (loading) {
    return <div style={{ padding: "10px 14px", fontSize: 11, color: "#aaa" }}>Calculando costo…</div>;
  }
  if (err) {
    return <div style={{ padding: "10px 14px", fontSize: 11, color: "#e74c3c" }}>Error: {err}</div>;
  }
  if (!data) return null;

  if (!data.hasProcesses) {
    return (
      <div style={{
        padding: "10px 14px", fontSize: 11, color: "#cbd5e1",
        background: "#1a1a1a", borderTop: "1px dashed #2e2e2e",
      }}>
        🔍 Catálogo de la pieza aún no tiene procesos definidos →
        no hay costo calculable. Precio actual catálogo: <strong style={{ color: "#aaa" }}>{fmtCLP(data.catalogPrice)}</strong>
      </div>
    );
  }

  if (!data.hasConstants) {
    return (
      <div style={{
        padding: "10px 14px", fontSize: 11, color: "#f39c12",
        background: "#2d2000", borderTop: "1px dashed #4a3500",
      }}>
        ⚠ Faltan tarifas en /settings → Precios. Configurá las tarifas por máquina para activar el cálculo de costo.
      </div>
    );
  }

  const delta = data.delta;
  const cataloHigher = delta.absolute > 0;
  const matchExact = delta.absolute === 0;
  // Color del delta:
  //   - rojo si catálogo está MUY abajo del calculado (subir precio)
  //   - amarillo si catálogo está apenas abajo
  //   - verde si catálogo está por arriba (margen extra)
  const deltaColor =
    delta.percent === null ? "#888"
    : delta.percent < -10 ? "#e74c3c"  // catálogo muy bajo → urge subir
    : delta.percent < 0   ? "#f39c12"  // ligeramente bajo
    : matchExact          ? "#888"
    : "#2ecc71";           // catálogo por arriba → margen sano

  return (
    <div style={{
      borderTop: "1px dashed #2e2e2e",
      background: "#0d0d0d",
      padding: "10px 14px",
    }}>
      <div style={{
        fontSize: 10, color: "#cbd5e1", textTransform: "uppercase",
        letterSpacing: ".06em", marginBottom: 6,
      }}>
        💰 Análisis de costo (referencial — no modifica catálogo)
      </div>

      <div className="grid" style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
        gap: 8, fontSize: 11,
      }}>
        <div>
          <div style={{ color: "#aaa", fontSize: 10 }}>Calculado por procesos</div>
          <div style={{ color: "#3498db", fontWeight: 700, fontSize: 13 }}>
            {fmtCLP(data.totals.finalCalculated)}
          </div>
          <div style={{ color: "#999", fontSize: 9, marginTop: 1 }}>
            procesos {fmtCLP(data.totals.processesCost)} + material {fmtCLP(data.totals.materialCost)}
            {data.profitPct > 0 && ` + ${data.profitPct}% margen`}
          </div>
        </div>

        <div>
          <div style={{ color: "#aaa", fontSize: 10 }}>Precio actual catálogo</div>
          <div style={{ color: "#aaa", fontWeight: 700, fontSize: 13 }}>
            {fmtCLP(data.catalogPrice)}
          </div>
        </div>

        <div>
          <div style={{ color: "#aaa", fontSize: 10 }}>Diferencia</div>
          <div style={{ color: deltaColor, fontWeight: 700, fontSize: 13 }}>
            {matchExact ? "—" : `${delta.absolute > 0 ? "+" : ""}${fmtCLP(delta.absolute)}`}
            {delta.percent !== null && !matchExact && (
              <span style={{ fontSize: 11, marginLeft: 4, opacity: 0.85 }}>
                ({delta.percent > 0 ? "+" : ""}{delta.percent}%)
              </span>
            )}
          </div>
          <div style={{ color: "#999", fontSize: 9, marginTop: 1 }}>
            {matchExact ? "calza con calculado"
              : cataloHigher ? "catálogo > calculado (margen extra)"
              : "catálogo < calculado (revisar)"}
          </div>
        </div>
      </div>

      {/* breakdown por proceso */}
      <details style={{ marginTop: 8 }}>
        <summary style={{ fontSize: 10, color: "#cbd5e1", cursor: "pointer" }}>
          Ver desglose por proceso ({data.breakdown.length})
        </summary>
        <div style={{ marginTop: 6, fontSize: 10, color: "#aaa" }}>
          {data.breakdown.map((b, i) => (
            <div key={b._id || i} style={{
              display: "flex", justifyContent: "space-between",
              padding: "2px 0", borderBottom: i === data.breakdown.length - 1 ? "none" : "1px dashed #1a1a1a",
            }}>
              <span>
                {b.external ? "🌐" : "⚙"} {b.name}
                {b.detail.type === "internal" && (
                  <span style={{ color: "#999", marginLeft: 6 }}>
                    {b.detail.machineType} · {b.detail.durationMinutes}min × ${b.detail.tariffPerHour}/h
                  </span>
                )}
              </span>
              <span style={{ fontFamily: "monospace", color: "#3498db" }}>
                {fmtCLP(b.cost)}
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

const WorkOrderProcessesPanel = ({ workOrderId, onPiUpdated }) => {
  const [pis,         setPis]         = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  // Click al título expande la pieza. Default: solo la pieza en proceso queda
  // expandida automáticamente (si hay alguna) — el resto colapsado para
  // navegar más rápido en OTs con muchas piezas.
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpand = id => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll   = () => setExpanded(new Set(pis.map(p => p._id)));
  const collapseAll = () => setExpanded(new Set());

  // ── carga ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!workOrderId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await backend.get(`pieceInstance?workOrder=${workOrderId}`);
      // backend devuelve { items: [...] } o array directo según la versión
      const items = Array.isArray(data) ? data : (data.items || data.pieceInstances || []);
      setPis(items);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Error desconocido";
      setError(msg);
    }
    setLoading(false);
  }, [workOrderId]);

  useEffect(() => { load(); }, [load]);

  // Auto-expandir pieza en proceso al cargar (si hay solo una)
  useEffect(() => {
    if (pis.length === 0) return;
    const inProcess = pis.filter(p => p.status === "in_process");
    if (inProcess.length === 1) {
      setExpanded(prev => prev.size === 0 ? new Set([inProcess[0]._id]) : prev);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pis.length]);

  // ── acción sobre un proceso ──────────────────────────────────────────────
  const callAction = async (pi, idx, action, extra = {}) => {
    try {
      const { data } = await backend.patch(
        `pieceInstance/${pi._id}/processes/${idx}`,
        { action, ...extra }
      );
      const updated = data.pieceInstance;
      setPis(prev => prev.map(p => p._id === pi._id ? { ...p, ...updated } : p));
      onPiUpdated?.(updated);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message;
      alert(`Error: ${msg}`);
    }
  };

  const handleProcAction = (pi, idx, proc, done, inProgress) => {
    if (done) {
      // Un click directo sobre completado solicita desmarcar
      setConfirmAction({
        message: `¿Deshacer "${proc.name}" como completado?`,
        detail: "El proceso vuelve a estar en proceso. Use con cuidado.",
        onConfirm: async () => {
          setConfirmAction(null);
          await callAction(pi, idx, "uncomplete");
        },
      });
      return;
    }
    if (inProgress) {
      const isLast = idx === pi.processes.length - 1;
      // C21 F2 — al cerrar se pregunta cuánto tomó de verdad, PRELLENADO con el teórico.
      // Prellenar no es un atajo: si la mayoría de las veces sale como estaba previsto,
      // pedirle al operario que escriba el mismo número cada vez termina en que escriba
      // cualquiera. Corrige el que difiere, que es el dato que sirve.
      const unidad = proc.isExternal ? "días" : "minutos";
      const teorico = proc.theoreticalDuration;
      setConfirmAction({
        message: `¿Marcar "${proc.name}" como completado?`,
        detail: isLast
          ? "Es el último proceso. La pieza pasa a 'Lista'."
          : "Auto-arranca el siguiente proceso.",
        onConfirm: async () => {
          setConfirmAction(null);
          let real;
          if (teorico != null) {
            const dicho = window.prompt(
              `¿Cuánto tomó "${proc.name}"? (${unidad})\n\n` +
              `Lo cotizado fueron ${teorico} ${unidad}. Si salió así, dejalo como está.`,
              String(teorico)
            );
            if (dicho === null) return;          // canceló: no se completa nada
            const n = Number(dicho);
            if (!Number.isFinite(n) || n < 0) {
              alert("Ese tiempo no se entiende. El proceso no se cerró.");
              return;
            }
            real = n;
          }
          await callAction(pi, idx, "complete", real !== undefined ? { realDuration: real } : {});
        },
      });
      return;
    }
    // pending → start
    setConfirmAction({
      message: `¿Iniciar "${proc.name}"?`,
      onConfirm: async () => {
        setConfirmAction(null);
        await callAction(pi, idx, "start");
      },
    });
  };

  // ── render ───────────────────────────────────────────────────────────────
  if (loading && pis.length === 0) {
    return (
      <div style={{ padding: "12px 16px", fontSize: 12, color: "#cbd5e1" }}>
        Cargando procesos…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "12px 16px", fontSize: 12, color: "#e74c3c" }}>
        Error: {error}
      </div>
    );
  }
  if (pis.length === 0) {
    return (
      <div style={{ padding: "12px 16px", fontSize: 12, color: "#aaa", fontStyle: "italic" }}>
        Sin piezas / instancias creadas para esta OT.
      </div>
    );
  }

  const allExpanded = expanded.size === pis.length;

  return (
    <div style={{ marginTop: 4 }}>
      {/* Toggle expand/collapse all */}
      {pis.length > 1 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
          <button
            onClick={allExpanded ? collapseAll : expandAll}
            style={{
              background: "transparent", border: "1px solid #2e2e2e",
              color: "#cbd5e1", padding: "3px 8px", borderRadius: 4,
              fontSize: 10, cursor: "pointer",
            }}
            title={allExpanded ? "Colapsar todas las piezas" : "Expandir todas las piezas"}
          >
            {allExpanded ? "▼ colapsar todo" : "▶ expandir todo"}
          </button>
        </div>
      )}

      {pis.map(pi => {
        const procs = pi.processes || [];
        const completedCount = procs.filter(p => p.completedAt).length;
        const total = procs.length;
        const statusColor = STATUS_COLOR[pi.status] || "#888";
        const isOpen = expanded.has(pi._id);

        return (
          <div
            key={pi._id}
            style={{
              border: "1px solid #2e2e2e",
              borderRadius: 6,
              marginBottom: 10,
              background: "#161616",
              overflow: "hidden",
            }}
          >
            {/* header del PI — clickable para expandir/colapsar */}
            <div
              onClick={() => toggleExpand(pi._id)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                borderBottom: isOpen ? "1px dashed #2e2e2e" : "none",
                background: isOpen ? "#1a1a1a" : "#161616",
                cursor: "pointer",
                userSelect: "none",
              }}
              title={isOpen ? "Click para colapsar" : "Click para expandir y editar procesos"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#999", width: 12 }}>
                  {isOpen ? "▼" : "▶"}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#ddd" }}>
                  {pi.pieceNameSnapshot || "(sin nombre)"}
                </span>
                {pi.quantity > 1 && (
                  <span style={{ fontSize: 11, color: "#cbd5e1" }}>× {pi.quantity}</span>
                )}
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  padding: "2px 7px", borderRadius: 9,
                  background: statusColor + "22",
                  color: statusColor,
                  border: `1px solid ${statusColor}55`,
                }}>
                  {STATUS_LABEL[pi.status] || pi.status}
                </span>
                {total > 0 && (
                  <span style={{ fontSize: 10, color: "#aaa" }}>
                    {completedCount}/{total} procesos
                  </span>
                )}
                {total === 0 && (
                  <span style={{ fontSize: 10, color: "#f39c12", fontStyle: "italic" }}>
                    sin procesos definidos
                  </span>
                )}
              </div>
            </div>

            {/* Contenido (procesos + costo + control dimensional) — solo si expandido */}
            {isOpen && (
              <>
                <PipelineVertical
                  pi={pi}
                  onProcAction={(idx, proc, done, inProgress) =>
                    handleProcAction(pi, idx, proc, done, inProgress)
                  }
                  onPipelineDefined={() => load()}
                />

                <CostAnalysisBox pieceId={pi.piece} />

                <DimensionalControlPanel
                  pi={pi}
                  onSaved={updated => {
                    setPis(prev => prev.map(p => p._id === pi._id ? { ...p, ...updated } : p));
                    onPiUpdated?.(updated);
                  }}
                />
              </>
            )}
          </div>
        );
      })}

      {/* Confirmación inline */}
      <ConfirmarAccion
        open={!!confirmAction}
        message={confirmAction?.message}
        detail={confirmAction?.detail}
        onConfirm={confirmAction?.onConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
};

export default WorkOrderProcessesPanel;
